import type { Brand } from '../brand';

interface BrandCapabilities {
  checkout: {
    /** Inventory includes pre-owned vehicles, so newness and mileage are worth showing.
     *  Every Slate vehicle is new with no meaningful mileage, so both are noise there. */
    sellsPreOwnedVehicles: boolean;
    hasVehicleDetailPages: boolean;
    advertisesNoDealerFees: boolean;
    /** The vehicle is configured on the brand's own site, so checkout links back to edit it. */
    editsVehicleOnBrandSite: boolean;
    sellsAccessories: boolean;
    advertisesZeroDueAtOrder: boolean;
    offersMoneyBackGuarantee: boolean;
    inspectsVehicles: boolean;
  };
  cart: {
    offersDownPaymentRecirculation: boolean;
    shipsFromNewCarDealerships: boolean;
    /** Slate keeps the self-arranged finance view, but does not offer it from the
     *  payment-type view — the tile only appears once it is the selected option. */
    allowsSelfArrangedFinancing: boolean;
    offersPartnerLenderPicker: boolean;
    /** Whether the finance tile may cite how many customers choose financing. */
    citesFinancingPopularity: boolean;
  };
  vehiclePages: {
    comparesVehicles: boolean;
  };
  navigation: {
    offersSupportContact: boolean;
    /** Editing a placed order releases the scheduled appointment slot and puts the
     *  vehicle back on the market under a timer, so the warning has to say so. */
    releasesAppointmentOnEdit: boolean;
    /** Checkout.UI serves the Root insurance quote under the order-placed URLs. Slate never
     *  reached it there (those URLs went to the OPD MFE), and keeps that until product says otherwise. */
    servesOrderPlacedInsuranceQuote: boolean;
  };
}

const BRAND_CAPABILITIES: Record<Brand, BrandCapabilities> = {
  carvana: {
    checkout: {
      sellsPreOwnedVehicles: true,
      hasVehicleDetailPages: true,
      advertisesNoDealerFees: true,
      editsVehicleOnBrandSite: false,
      sellsAccessories: false,
      advertisesZeroDueAtOrder: true,
      offersMoneyBackGuarantee: true,
      inspectsVehicles: true
    },
    cart: {
      offersDownPaymentRecirculation: true,
      shipsFromNewCarDealerships: true,
      allowsSelfArrangedFinancing: true,
      offersPartnerLenderPicker: true,
      citesFinancingPopularity: true
    },
    vehiclePages: {
      comparesVehicles: true
    },
    navigation: {
      offersSupportContact: true,
      releasesAppointmentOnEdit: true,
      servesOrderPlacedInsuranceQuote: true
    }
  },
  slate: {
    checkout: {
      sellsPreOwnedVehicles: false,
      hasVehicleDetailPages: false,
      advertisesNoDealerFees: false,
      editsVehicleOnBrandSite: true,
      sellsAccessories: true,
      advertisesZeroDueAtOrder: false,
      offersMoneyBackGuarantee: false,
      inspectsVehicles: false
    },
    cart: {
      offersDownPaymentRecirculation: false,
      shipsFromNewCarDealerships: false,
      allowsSelfArrangedFinancing: false,
      offersPartnerLenderPicker: false,
      citesFinancingPopularity: false
    },
    vehiclePages: {
      comparesVehicles: false
    },
    navigation: {
      offersSupportContact: false,
      releasesAppointmentOnEdit: false,
      servesOrderPlacedInsuranceQuote: false
    }
  }
};

export { BRAND_CAPABILITIES };
export type { BrandCapabilities };
